import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import {
    selectAwaitingActivity,
    selectTicketState,
} from 'flow/selectors/TicketsSelectors';
import { TicketKey } from 'flow/types/EntityKeys';

type Props = {
    ticketKey: TicketKey;
};

const AwaitingActivityBadge = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();

    const awaitingActivity = useAppSelector((state) =>
        selectAwaitingActivity(state, ticketKey),
    );
    const ticketStatus = useAppSelector((state) =>
        selectTicketState(state, ticketKey),
    );

    const isClosed = ticketStatus === 'closed';

    // Negative end-margin cancels the parent's gap-x-2 when the badge is
    // collapsed, so the contact name sits flush against the channel icon
    // and slides 10px sideways as the condition flips. The dot fades and
    // scales from its own centre (no overflow clipping) over the same
    // 150ms ease-out. The 50ms delays sequence the two halves so the dot
    // always sits inside an open gap: marking opens the gap first, then
    // the dot fades in (delay on the dot); unmarking fades the dot out
    // first, then closes the gap (delay on the width/margin).
    return (
        <div
            aria-hidden={!awaitingActivity}
            className={twMerge(
                'flex shrink-0 items-center transition-all duration-150 ease-out',
                awaitingActivity ? 'me-0 w-2' : '-me-2 w-0 delay-[50ms]',
            )}
        >
            <div
                className={twMerge(
                    'size-2 shrink-0 rounded-full transition-all duration-150 ease-out',
                    isClosed ? 'bg-gray-400' : 'bg-red-500',
                    awaitingActivity
                        ? 'scale-100 opacity-100 delay-[50ms]'
                        : 'pointer-events-none scale-[0.7] opacity-0',
                )}
            />
            {awaitingActivity ? (
                <span className="sr-only">
                    {i18next.t('tickets.filter.marked')}
                </span>
            ) : null}
        </div>
    );
};

export default React.memo(AwaitingActivityBadge);
